import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBankAccounts, createBankAccount, deleteBankAccount } from '../services/rentalCollectionsService';
import PageWrapper from '../components/PageWrapper';
import BankAccountModal from '../components/BankAccountModal';

const BankAccountList = () => {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    console.log('BankAccountList: Component mounted, loading accounts...');
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      console.log('BankAccountList: Starting to load accounts...');
      setLoading(true);
      const accountData = await getBankAccounts();
      console.log('BankAccountList: Loaded accounts:', accountData);
      setAccounts(accountData);
    } catch (error) {
      console.error('Error loading accounts:', error);
      // Add user-friendly error message
      alert('Failed to load bank accounts. Please check your connection and try again.');
    } finally {
      setLoading(false);
      console.log('BankAccountList: Finished loading accounts');
    }
  };

  const handleCreateAccount = () => {
    setShowAccountModal(true);
  };

  const handleEditAccount = (account) => {
    navigate(`/bankList/edit/${account.id}`);
  };

  const handleSaveAccount = async (accountData) => {
    try {
      await createBankAccount(accountData);
      await loadAccounts();
      setShowAccountModal(false);
    } catch (error) {
      console.error('Error saving account:', error);
      alert('Error creating account. Please try again.');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await deleteBankAccount(accountId);
        loadAccounts();
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Error deleting account. Please try again.');
      }
    }
  };

  // Filter accounts based on search and type
  const filteredAccounts = (accounts || []).filter(account => {
    const matchesSearch =
      account.account_holder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.bank_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.account_number?.includes(searchTerm);

    const matchesType = filterType === 'all' || account.account_type === filterType;

    return matchesSearch && matchesType;
  });

  const accountTypes = ['checking', 'savings', 'business'];
  const accountTypeStats = accountTypes.map(type => ({
    type,
    count: (accounts || []).filter(acc => acc.account_type === type).length || 0
  }));

  if (loading) {
    return (
      <PageWrapper>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading your bank accounts...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="container-fluid py-4">
        {/* Modern Header with Gradient Background */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px'
            }}>
              <div className="card-body text-white p-5">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-white bg-opacity-20 rounded-circle p-3 me-3">
                        <i className="bi bi-bank display-6 text-primary"></i>
                      </div>
                      <div>
                        <h1 className="mb-2 fw-bold">Bank Accounts</h1>
                        <p className="mb-0 opacity-90 fs-5">Manage your rental collection accounts</p>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="row g-3 mt-3">
                      <div className="col-sm-4">
                        <div className="bg-white bg-opacity-75 rounded-3 p-3 text-center shadow-sm">
                          <h3 className="mb-1 fw-bold">{accounts.length}</h3>
                          <small className="opacity-90">Total Accounts</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 text-end">
                    <button
                      className="btn btn-light btn-lg px-4 py-3 rounded-pill shadow"
                      onClick={handleCreateAccount}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add New Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Filters and Search */}
        <div className="row mb-4">
          <div className="col-md-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row g-3 align-items-center">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder="Search accounts, banks, or account numbers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <option value="all">All Account Types</option>
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                      <option value="business">Business</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <span className="text-muted small">
                      {filteredAccounts.length} of {accounts?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            {/* Account Type Summary */}
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="mb-3">Account Types</h6>
                <div className="row g-2">
                  {accountTypeStats.map(stat => (
                    <div key={stat.type} className="col-4">
                      <div className="text-center">
                        <div className="fw-bold text-primary">{stat.count}</div>
                        <small className="text-muted text-capitalize">{stat.type}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Account Cards Grid */}
        <div className="row">
          {filteredAccounts.length > 0 ? (
            filteredAccounts.map((account) => (
              <div key={account.id} className="col-lg-6 col-xl-4 mb-4">
                <div className="card border-0 shadow-sm h-100 hover-lift" style={{
                  borderRadius: '16px',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Account Header with Bank Logo Placeholder */}
                  <div className="card-header border-0 bg-light" style={{
                    borderRadius: '16px 16px 0 0',
                    background: 'linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%)'
                  }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                          <i className="bi bi-bank2 text-primary"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">{account.account_holder}</h6>
                          <small className="text-muted">{account.bank_name}</small>
                        </div>
                      </div>
                      <span className={`badge rounded-pill px-3 py-2 ${
                        account.account_type === 'business' ? 'bg-warning' :
                        account.account_type === 'savings' ? 'bg-success' : 'bg-primary'
                      }`}>
                        {account.account_type?.charAt(0).toUpperCase() + account.account_type?.slice(1)}
                      </span>
                    </div>
                  </div>

                  <div className="card-body">
                    {/* Account Details */}
                    <div className="mb-4">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2">
                            <span className="text-muted small">Account Number</span>
                            <span className="font-monospace fw-medium">
                              ****{account.account_number?.slice(-4)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Activity Indicator */}
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded-2">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-activity text-primary me-2"></i>
                          <small className="text-muted">Recent Activity</small>
                        </div>
                        <span className="badge bg-success-subtle text-success">
                          Active
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Footer */}
                  <div className="card-footer bg-white border-0" style={{ borderRadius: '0 0 16px 16px' }}>
                    <div className="d-grid gap-2 d-md-flex">
                      <button
                        className="btn btn-outline-primary flex-fill rounded-pill"
                        onClick={() => handleEditAccount(account)}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Manage
                      </button>
                      <button
                        className="btn btn-outline-danger rounded-circle"
                        onClick={() => handleDeleteAccount(account.id)}
                        title="Delete Account"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                <div className="card-body text-center py-5">
                  <div className="bg-light rounded-circle d-inline-flex p-4 mb-4">
                    <i className="bi bi-bank display-3 text-muted"></i>
                  </div>
                  <h3 className="text-muted mb-3">
                    {searchTerm || filterType !== 'all' ? 'No Matching Accounts' : 'No Bank Accounts Yet'}
                  </h3>
                  <p className="text-muted mb-4 fs-5">
                    {searchTerm || filterType !== 'all' ?
                      'Try adjusting your search or filter criteria.' :
                      'Get started by adding your first rental collection account.'
                    }
                  </p>
                  {(!searchTerm && filterType === 'all') && (
                    <button
                      className="btn btn-primary btn-lg px-5 py-3 rounded-pill"
                      onClick={handleCreateAccount}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Your First Account
                    </button>
                  )}
                  {(searchTerm || filterType !== 'all') && (
                    <button
                      className="btn btn-outline-primary rounded-pill px-4"
                      onClick={() => {
                        setSearchTerm('');
                        setFilterType('all');
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bank Account Modal */}
      <BankAccountModal
        show={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        account={null}
        onSave={handleSaveAccount}
      />

      {/* Custom CSS for hover effects */}
      <style dangerouslySetInnerHTML={{__html: `
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
        }
      `}} />
    </PageWrapper>
  );
};

export default BankAccountList;
